// parts/PracticalMaterialLibrarySidebar.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Sidebar } from 'primereact/sidebar';
import { ProgressSpinner } from 'primereact/progressspinner';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { PracticalMaterial } from '../PracticalMaterials';

export interface LibraryMaterial extends PracticalMaterial {
  course_title: string | null;
  chapter_title: string | null;
}

interface PracticalMaterialLibrarySidebarProps {
  visible: boolean;
  onHide: () => void;
  onReuse: (material: LibraryMaterial) => void;
}

const PAGE_LIMIT = 20;

const OwnerBadge: React.FC<{ material: LibraryMaterial }> = ({ material }) => {
  const isInstitute = material.owner_level === 'institute';
  return (
    <span
      style={{
        fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: '10px',
        background: isInstitute ? '#fef3c7' : '#e0f2fe',
        color: isInstitute ? '#92400e' : '#075985',
        textTransform: 'uppercase', letterSpacing: '0.3px',
      }}
    >
      {isInstitute ? 'Institute' : 'Branch'}
    </span>
  );
};

const PracticalMaterialLibrarySidebar: React.FC<PracticalMaterialLibrarySidebarProps> = ({ visible, onHide, onReuse }) => {
  const [items, setItems]     = useState<LibraryMaterial[]>([]);
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchLibrary = useCallback(async (targetPage: number, targetSearch: string, append: boolean) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/v1/practical-materials/library', {
        params: { search: targetSearch, page: targetPage, limit: PAGE_LIMIT },
      });
      if (response.data.success) {
        setItems((prev) => (append ? [...prev, ...response.data.data] : response.data.data));
        setTotal(response.data.total ?? 0);
        setPage(targetPage);
      } else {
        setError('Failed to load materials library');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load materials library');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!visible) return;
    fetchLibrary(1, search, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchLibrary(1, value, false);
    }, 400);
  };

  const handleLoadMore = () => fetchLibrary(page + 1, search, true);

  const hasMore = items.length < total;

  return (
    <Sidebar
      visible={visible}
      onHide={onHide}
      position="right"
      showCloseIcon={false}
      style={{ width: '440px' }}
      pt={{ content: { style: { padding: 0 } }, header: { style: { padding: 0, display: 'none' } } }}
    >
      {/* Header */}
      <div
        style={{
          background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
          padding: '28px 24px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '160px', height: '160px', background: 'rgba(212, 241, 126, 0.1)', borderRadius: '50%', filter: 'blur(40px)' }} />
        <button
          type="button"
          onClick={onHide}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: '18px',
            right: '18px',
            zIndex: 2,
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '8px',
            color: 'white',
            cursor: 'pointer',
          }}
        >
          <i className="pi pi-times" style={{ fontSize: '0.9rem' }} />
        </button>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'inline-block', padding: '5px 12px', background: 'rgba(212, 241, 126, 0.2)', borderRadius: '20px', marginBottom: '10px', fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
            Practical Material Management
          </div>
          <h2 style={{ margin: '0 0 6px 0', fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.5px' }}>
            Reuse an Existing Material
          </h2>
          <p style={{ margin: 0, color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>
            Attach files and videos already uploaded elsewhere, without re-uploading
          </p>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '20px 24px', background: '#fafafa', minHeight: 'calc(100% - 118px)' }}>
        <div style={{ marginBottom: '16px' }}>
          <GlobalInputField
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by title..."
            icon="pi pi-search"
          />
        </div>

        {error && (
          <div style={{ padding: '12px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', color: '#b91c1c', fontSize: '0.82rem', marginBottom: '16px' }}>
            <i className="pi pi-exclamation-circle" style={{ marginRight: '6px' }} />
            {error}
          </div>
        )}

        {loading && items.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 0' }}>
            <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="3" />
            <p style={{ marginTop: '14px', color: '#0f3d3e', fontSize: '0.85rem' }}>Loading library...</p>
          </div>
        ) : items.length === 0 ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', background: 'white', borderRadius: '16px', border: '2px dashed #e0e0e0' }}>
            <i className="pi pi-inbox" style={{ fontSize: '2.5rem', color: '#ddd', marginBottom: '12px', display: 'block' }} />
            <h3 style={{ margin: '0 0 6px 0', color: '#666', fontSize: '0.95rem', fontWeight: 600 }}>No materials found</h3>
            <p style={{ margin: 0, color: '#999', fontSize: '0.82rem' }}>Try a different search term</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {items.map((material) => (
              <div
                key={material.id}
                style={{
                  padding: '14px',
                  border: '1px solid #f0f0f0',
                  borderRadius: '12px',
                  background: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1a1a1a', lineHeight: 1.3 }}>
                    {material.title}
                  </span>
                  <OwnerBadge material={material} />
                </div>

                <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '10px' }}>
                  <i className="pi pi-book" style={{ marginRight: '5px' }} />
                  {material.course_title || 'Unknown course'}
                  {material.chapter_title ? ` · ${material.chapter_title}` : ' · General'}
                </div>

                <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '12px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {material.files_count > 0 && (
                    <span><i className="pi pi-paperclip" style={{ marginRight: '4px' }} />{material.files_count} file{material.files_count !== 1 ? 's' : ''}</span>
                  )}
                  {material.videos_count > 0 && (
                    <span style={{ color: '#16a34a', fontWeight: 600 }}><i className="pi pi-play-circle" style={{ marginRight: '4px' }} />{material.videos_count} video{material.videos_count !== 1 ? 's' : ''}</span>
                  )}
                  {material.files_count === 0 && material.videos_count === 0 && (
                    <span style={{ color: '#bbb' }}>No attachments</span>
                  )}
                </div>

                <GlobalButton
                  label="Use This Material"
                  variant="secondary"
                  icon="pi pi-copy"
                  onClick={() => onReuse(material)}
                  fullWidth
                />
              </div>
            ))}

            {hasMore && (
              <button
                type="button"
                onClick={handleLoadMore}
                disabled={loading}
                style={{
                  marginTop: '4px',
                  padding: '10px',
                  background: 'transparent',
                  border: '1px solid #e0e0e0',
                  borderRadius: '10px',
                  color: '#0f3d3e',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? (
                  <><i className="pi pi-spin pi-spinner" style={{ marginRight: '6px' }} />Loading...</>
                ) : (
                  <><i className="pi pi-angle-down" style={{ marginRight: '6px' }} />Load More</>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default PracticalMaterialLibrarySidebar;